import gql from 'graphql-tag';
import { cloneDeep } from 'lodash-es';
import { ref, computed } from 'vue';
import { route } from '@router/router';
import { useQuery } from '@vue/apollo-composable';
import { useProfileList } from '@models/profile/profileList';

const GET_PACKAGEINFO_QUERY = gql`
    query getProfilePackageFromNewPtengine($profileId: String!) {
        getProfilePackageFromNewPtengine(profileId: $profileId) {
            sid
            packageId
            startTime
            endTime
            status
            needPopupTrial
            isPtxPlan
            privilege {
                monthlyActiveUsers
                userGroups
                goals
                campaigns
                useABTest
                useCodeMode
                useCSVDownload
                useAI
                hidePopupPtxLogo
            }
            usage {
                monthlyActiveUsers
                userGroups
                goals
                campaigns
            }
            packageBaseConf {
                id
                packageId
                packageName
                area
                level
                originalPrice
                currency
                payType
                isCustomized
                validFlag
                packageVersion
                packageType
                packagePrivilegeConfList {
                    id
                    operator
                    remarks
                    packageId
                    key
                    value
                    valueType
                }
            }
            ptiUsage {
                totalPV
                totalEvent
                consumedPV
                consumedEvent
            }
        }
    }
`;

export interface OriginPackageInfo {
    sid: string;
    packageId: number;
    startTime: number;
    endTime: number;
    status: number;
    isPtxPlan?: boolean;
    privilege: {
        monthlyActiveUsers: number;
        userGroups: number;
        goals: number;
        campaigns: number;
        useABTest: boolean;
        useCaodeMode: boolean;
        useCSVDownload: boolean;
        useAI: boolean;
        hidePopupPtxLogo: boolean;
    };
    usage: {
        monthlyActiveUsers: number;
        userGroups: number;
        goals: number;
        campaigns: number;
    };
    packageBaseConf: {
        id: number;
        packageId: number;
        packageName: string;
        area: number;
        level: number;
        originalPrice: number;
        currency: string;
        payType: number;
        isCustomized: number;
        validFlag: number;
        packageVersion: number;
        packageType: number;
        packagePrivilegeConfList: {
            id: number;
            operator: string;
            remarks: string;
            packageId: number;
            key: string;
            value: number;
            valueType: number;
        };
    };
    ptiUsage: {
        totalPV: number;
        totalEvent: number;
        consumedPV: number;
        consumedEvent: number;
    };
    needPopupTrial: boolean;
}

export interface PackageInfo extends OriginPackageInfo {
    /** 套餐UU使用比例数 */
    experienceUsageRate: number;
    insightPvRate: number;
    insightEventRate: number;
}

const packageInfoResult = ref();
const profilePackageInfoLoading = ref(false);
const { profileList } = useProfileList();

export function usePackageInfo() {
    const getProfilePackageInfo = (params?: {
        sid?: string;
        resultCallback?: (T: OriginPackageInfo) => void;
        rejectCallback?: (err: any) => void;
        finallyCallback?: (T: any) => void;
    }) => {
        const profileId = params?.sid || route.value.params?.sid;
        if (!profileId) return;

        profilePackageInfoLoading.value = true;

        const { onResult, onError } = useQuery<{
            getProfilePackageFromNewPtengine: OriginPackageInfo;
        }>(
            GET_PACKAGEINFO_QUERY,
            {
                profileId
            },
            {
                fetchPolicy: 'no-cache'
            }
        );

        onResult(data => {
            if (data?.data?.getProfilePackageFromNewPtengine) {
                packageInfoResult.value = data?.data?.getProfilePackageFromNewPtengine || {};
                params?.resultCallback &&
                    typeof params?.resultCallback === 'function' &&
                    params?.resultCallback(data.data.getProfilePackageFromNewPtengine);
                params?.finallyCallback &&
                    typeof params?.finallyCallback === 'function' &&
                    params?.finallyCallback(data.data.getProfilePackageFromNewPtengine);
            }
            profilePackageInfoLoading.value = false;
        });

        onError(err => {
            packageInfoResult.value = undefined;
            profilePackageInfoLoading.value = false;
            params?.rejectCallback &&
                typeof params?.rejectCallback === 'function' &&
                params?.rejectCallback(err);
            params?.finallyCallback &&
                typeof params?.finallyCallback === 'function' &&
                params?.finallyCallback(err);
        });
    };
    const packageInfo = computed(() => {
        if (!packageInfoResult.value) return undefined;
        const packageInfo = cloneDeep(packageInfoResult.value) as unknown as PackageInfo;
        const UURate =
            (packageInfo.usage?.monthlyActiveUsers || 0) /
            (packageInfo.privilege?.monthlyActiveUsers || 0);
        packageInfo.experienceUsageRate = Number.isNaN(UURate) ? 0 : Math.ceil(UURate * 100);

        const PVRate: number =
            (packageInfo.ptiUsage?.consumedPV || 0) / (packageInfo.ptiUsage?.totalPV || 0);
        const EventRate: number =
            (packageInfo.ptiUsage?.consumedEvent || 0) / (packageInfo.ptiUsage?.totalEvent || 0);
        packageInfo.insightPvRate = Number.isNaN(PVRate) ? 0 : Math.ceil(PVRate * 100);
        packageInfo.insightEventRate = Number.isNaN(EventRate) ? 0 : Math.ceil(EventRate * 100);
        return packageInfo;
    });

    const currentOriginProfile = computed(() => {
        if (!profileList.value || !route.value?.params?.sid) return undefined;
        const searchedCurrentProfile = profileList.value?.find(
            profile => profile.sid === route.value.params.sid
        );
        if (!searchedCurrentProfile) return undefined;
        return cloneDeep(searchedCurrentProfile);
    });
    return {
        packageInfo,
        currentOriginProfile,
        profilePackageInfoLoading,
        getProfilePackageInfo
    };
}
